import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CircleAlert, LifeBuoy } from 'lucide-react';
import { Button, Dialog, Field, Input, Select } from '@/components/ui';
import { idempotencyKey } from '@/utils/idempotency';
import { useNodes } from '@/hooks/useNodes';
import { useRelocateSite } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatDateTime } from '@/utils/format';
import { canReceiveSite } from '@/utils/nodes';
import type { SiteDetail } from '@/types/api';

/**
 * "Поднять на другом сервере" (P8): the site's server is gone, and the site is
 * raised on another from a backup the panel holds. What that costs — whatever
 * changed after the backup — is said before the click, with the backup's date.
 */
export function RelocateSiteDialog({
  site,
  open,
  onClose,
}: {
  readonly site: SiteDetail;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const verified = site.backups.filter((backup) => backup.verification === 'verified');
  const [backupId, setBackupId] = useState('');
  const [target, setTarget] = useState('');
  const [typed, setTyped] = useState('');
  const key = useRef(idempotencyKey('site.relocate'));

  const nodes = useNodes({ enabled: open });
  const candidates = (nodes.data ?? []).filter((node) => canReceiveSite(node, site));
  const chosen = backupId || verified[0]?.id || '';
  const chosenBackup = verified.find((backup) => backup.id === chosen);

  const relocate = useRelocateSite(site.id, {
    onSuccess: () => {
      toast.success('Восстановление на другом сервере поставлено в очередь');
      key.current = idempotencyKey('site.relocate');
      setTyped('');
      onClose();
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!chosen || !typed.trim() || candidates.length === 0 || relocate.isPending) return;
    relocate.mutate({
      input: { backupId: chosen, ...(target ? { targetNodeId: target } : {}), confirmation: typed.trim() },
      key: key.current,
    });
  }

  return (
    <Dialog
      open={open}
      title="Восстановить на другом сервере"
      description={`Сервер ${site.nodeName ?? ''} не отвечает`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={relocate.isPending}>
            Отмена
          </Button>
          <Button
            type="submit"
            form="relocate-site"
            variant="primary"
            icon={LifeBuoy}
            busy={relocate.isPending}
            disabled={!chosen || !typed.trim() || candidates.length === 0}
          >
            Восстановить
          </Button>
        </>
      }
    >
      <form id="relocate-site" className="stack" onSubmit={submit} noValidate>
        {verified.length === 0 ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            Нет проверенной копии сайта.
          </p>
        ) : (
          <Field label="Из копии" htmlFor="relocate-backup" hint="Только копии, хранящиеся в панели или S3">
            <Select
              id="relocate-backup"
              label="Из копии"
              value={chosen}
              onChange={setBackupId}
              options={verified.map((backup) => ({
                value: backup.id,
                label: formatDateTime(backup.createdAt),
                hint: backup.offsiteConfirmed ? 'в S3' : 'в панели',
              }))}
            />
          </Field>
        )}

        {nodes.isSuccess && candidates.length === 0 ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            Нет других активных серверов.
          </p>
        ) : (
          <Field label="Куда" htmlFor="relocate-target">
            <Select
              id="relocate-target"
              label="Куда"
              value={target}
              onChange={setTarget}
              options={[
                { value: '', label: 'Автоматически', hint: 'Наименее загруженный подходящий' },
                ...candidates.map((node) => ({ value: node.id, label: node.name, hint: `${node.siteCount} сайт(ов)` })),
              ]}
            />
          </Field>
        )}

        <ol className="enroll-steps">
          <li>На выбранном сервере сайт восстанавливается из копии.</li>
          <li>После проверки DNS переключается на новый сервер.</li>
          <li>Когда прежний сервер вернётся, копия сайта на нём будет остановлена, но не удалена.</li>
        </ol>
        <p className="field-hint">
          {chosenBackup
            ? `Изменения после ${formatDateTime(chosenBackup.createdAt)} будут потеряны.`
            : 'Изменения после даты копии будут потеряны.'}
        </p>

        <Field label="Для подтверждения введите идентификатор сайта" htmlFor="relocate-confirm" hint={site.slug}>
          <Input id="relocate-confirm" value={typed} onChange={(event) => setTyped(event.target.value)} placeholder={site.slug} autoComplete="off" />
        </Field>

        {relocate.isError ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            {errorText(relocate.error, 'Не удалось запустить восстановление')}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
